import _extends from "@babel/runtime/helpers/extends";
import { FiExternalLink as _FiExternalLink } from "@react-icons/all-files/fi/FiExternalLink.js";
import _merge from "lodash/merge";
import _isEqual from "lodash/isEqual";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Box, Button, Flex, Heading } from 'core/components';
import getErrorBoundary from 'core/util/getErrorBoundary';
import UdeDataView from 'app/views/ude/UdeDataView';
import QueryModel from 'app/stores/ude/QueryModel';
import $ude from 'app/stores/ude/$ude';
import { useHistory } from 'react-router-dom';
const ErrorBoundary = getErrorBoundary('UdeDataViewWrapper');
const DEFAULT_SETTINGS = {
  chart: {
    enable: true,
    type: 'line',
    colors: 'default',
    legend: {
      enable: true,
      position: 'right',
      mode: 'condensed',
      showRollups: true,
      showEventsView: true
    },
    xAxis: {
      timezone: 'utc'
    },
    yAxis: {
      type: 'linear'
    }
  }
};
function UdeDataViewWrapperBase({
  query,
  settings: settingsOverride,
  padding = 1,
  showOpenInUde = false,
  onChartRef,
  legendMaxWidth,
  chartOverrides,
  title,
  refreshToken,
  emptyState,
  $auth,
  ...boxProps
}) {
  const history = useHistory();
  const hasMounted = useRef(false);

  // Gate on embedded_ude permission
  const hasEmbeddedUdePermission = $auth?.hasPermission('embedded_ude', {
    overrideForSudo: false
  });
  const canOpenInUde = showOpenInUde && $auth?.hasPermission('ude', {
    overrideForSudo: false
  });

  // Deep-merge caller's partial settings with defaults
  const settings = useMemo(() => settingsOverride ? _merge({}, DEFAULT_SETTINGS, settingsOverride) : DEFAULT_SETTINGS, [settingsOverride]);

  // Create QueryModel once per component lifetime
  const [queryModel] = useState(() => new QueryModel(query, settings));

  // Sync query when inputs change
  useEffect(() => {
    if (!_isEqual(queryModel.query, query)) {
      queryModel.query = query;
      queryModel.dirty = true;
    }
  }, [queryModel, query]);

  // Sync settings when inputs change
  useEffect(() => {
    if (!_isEqual(queryModel.settings, settings)) {
      queryModel.settings = settings;
      queryModel.dirty = true;
    }
  }, [queryModel, settings]);

  // Rerun the current query when the caller explicitly requests a refresh.
  useEffect(() => {
    if (hasMounted.current) {
      queryModel.run();
      return;
    }
    hasMounted.current = true;
  }, [queryModel, refreshToken]);

  // Cleanup on unmount
  useEffect(() => () => {
    queryModel.cleanup();
  }, [queryModel]);
  const handleOpenInUde = useCallback(() => {
    $ude.setPendingQuery({
      query: queryModel.query,
      chart: queryModel.settings.chart
    });
    history.push('/v4/ude');
  }, [queryModel, history]);
  if (!hasEmbeddedUdePermission) {
    return null;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, (title || canOpenInUde) && /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    minHeight: "24px"
  }, title ? /*#__PURE__*/React.createElement(Heading, {
    level: 5,
    m: 0
  }, title) : /*#__PURE__*/React.createElement(Box, null), canOpenInUde && /*#__PURE__*/React.createElement(Button, {
    minimal: true,
    small: true,
    icon: /*#__PURE__*/React.createElement(_FiExternalLink, {
      size: 14
    }),
    onClick: handleOpenInUde
  }, "Open in Universal Data Explorer")), /*#__PURE__*/React.createElement(Box, _extends({
    className: "ude-dataview-wrapper",
    height: "100%",
    mt: 1,
    position: "relative"
  }, boxProps), /*#__PURE__*/React.createElement(ErrorBoundary, null, /*#__PURE__*/React.createElement(UdeDataView, {
    queryModel: queryModel,
    padding: padding,
    onChartRef: onChartRef,
    legendMaxWidth: legendMaxWidth,
    chartOverrides: chartOverrides,
    emptyState: emptyState
  }))));
}
const UdeDataViewWrapper = inject('$auth')(observer(UdeDataViewWrapperBase));
export default UdeDataViewWrapper;
export { DEFAULT_SETTINGS };